// src/hooks/useCrewMembersPG.js
import { useState, useEffect, useCallback, useMemo } from 'react';
import { CrewMember } from '../models';
import { CrewMemberAPI } from '../services/api/crewMemberAPI';


// Cache management utilities
const CACHE_KEY = 'salon_crew_members_cache';
const TIMESTAMP_KEY = 'salon_crew_members_timestamp';

const loadCrewMembersFromStorage = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    return cached ? JSON.parse(cached) : null;
  } catch (error) {
    console.error('Error loading crew members from cache:', error);
    return null;
  }
};

const saveCrewMembersToStorage = (crewMembers) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(crewMembers));
    localStorage.setItem(TIMESTAMP_KEY, new Date().toISOString());
  } catch (error) {
    console.error('Error saving crew members to cache:', error);
  }
};

const clearCrewMembersCache = () => {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(TIMESTAMP_KEY);
  } catch (error) {
    console.error('Error clearing crew members cache:', error);
  }
};

export function useCrewMembersPG() {
  const [crewMembers, setCrewMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Convert raw data to CrewMember instances
  const crewMemberInstances = useMemo(() => {
    return crewMembers.map(member => CrewMember.fromJSON({
      ...member,
      // Ensure consistent field mapping
      id: member.id,
      name: member.name,
      role: member.role,
      phone: member.phone,
      email: member.email,
      assigned_services: member.assigned_services || [],
      is_active: member.is_active,
      color: member.color || '#3B82F6',
      availability: member.availability || {},
      created_at: member.created_at,
      updated_at: member.updated_at
    }));
  }, [crewMembers]);

  // Computed values
  const activeCrewMembers = crewMembers.filter(member => member.is_active);
  const inactiveCrewMembers = crewMembers.filter(member => !member.is_active);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  // Load all crew members (replaces Firebase autoLoad)
  const loadAllCrewMembers = useCallback(async (forceReload = false) => {
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      // Check local storage first (unless forced reload)
      if (!forceReload) {
        const cachedCrewMembers = loadCrewMembersFromStorage();
        if (cachedCrewMembers && cachedCrewMembers.length > 0) {
          const crewMemberInstances = cachedCrewMembers.map(member => CrewMember.fromJSON(member));
          setCrewMembers(crewMemberInstances);
          return crewMemberInstances;
        }
      }

      // Fetch from database if no cache or force reload
      const data = await CrewMemberAPI.getAll();
      const crewMemberInstances = data.map(member => CrewMember.fromJSON(member));

      // Save to cache
      saveCrewMembersToStorage(data);

      setCrewMembers(crewMemberInstances);
      return crewMemberInstances;
    } catch (err) {
      console.error('Error loading crew members:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loading]);

  // Load crew members on mount
  useEffect(() => {
    if (!hasLoadedOnce) {
      loadAllCrewMembers(false);
      setHasLoadedOnce(true);
    }
  }, [hasLoadedOnce, loadAllCrewMembers]);

  // CRUD Operations - maintaining same interface as Firebase hook
  const addCrewMember = useCallback(async (crewMemberData) => {
    setLoading(true);
    setError(null);

    try {
      // Validate the crew member data
      const validation = CrewMember.validate(crewMemberData);
      if (!validation.isValid) {
        throw new Error(validation.errors.join(', '));
      }

      // Check for duplicate names
      const existingMember = crewMemberInstances.find(
        member => member.name.toLowerCase() === crewMemberData.name.toLowerCase()
      );
      if (existingMember) {
        throw new Error('A crew member with this name already exists');
      }

      // Validate that assigned_services is provided
      if (!crewMemberData.assigned_services || crewMemberData.assigned_services.length === 0) {
        throw new Error('At least one service must be assigned to the crew member');
      }

      // Create new crew member
      const newCrewMember = await CrewMemberAPI.create(crewMemberData);
      const newCrewMemberInstance = CrewMember.fromJSON(newCrewMember);

      // Add to current state
      setCrewMembers(prev => [...prev, newCrewMember]);

      // Clear cache and reload all crew members
      clearCrewMembersCache();
      await loadAllCrewMembers(true);

      return newCrewMemberInstance;
    } catch (err) {
      console.error('Error adding crew member:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [loadAllCrewMembers]);

  const updateCrewMember = useCallback(async (crewMemberId, updateData) => {
    setLoading(true);
    setError(null);

    // Store original state for potential rollback (like customers)
    const originalCrewMembers = [...crewMembers];

    try {
      // Find the crew member
      const existingCrewMemberIndex = crewMembers.findIndex(m => m.id === crewMemberId);
      if (existingCrewMemberIndex === -1) {
        throw new Error('Crew member not found');
      }

      const existingCrewMember = crewMembers[existingCrewMemberIndex];

      // Validate update data if it includes assigned_services
      if (updateData.assigned_services !== undefined) {
        const validation = CrewMember.validate({
          ...existingCrewMember,
          ...updateData
        });
        if (!validation.isValid) {
          throw new Error(validation.errors.join(', '));
        }
      }

      // Check for duplicate names if name is being updated
      if (updateData.name && updateData.name !== existingCrewMember.name) {
        const duplicateMember = crewMemberInstances.find(
          member => member.id !== crewMemberId &&
            member.name.toLowerCase() === updateData.name.toLowerCase()
        );
        if (duplicateMember) {
          throw new Error('A crew member with this name already exists');
        }
      }

      // Optimistic update: Update local state immediately for better UX (like customers)
      const optimisticUpdatedCrewMember = { ...existingCrewMember, ...updateData };
      const optimisticUpdatedCrewMembers = [...crewMembers];
      optimisticUpdatedCrewMembers[existingCrewMemberIndex] = optimisticUpdatedCrewMember;

      setCrewMembers(optimisticUpdatedCrewMembers);
      console.log('CrewMember Hook: Applied optimistic update to local state');

      try {
        // Make the API call (single call, like appointments)
        console.log('CrewMember Hook: Updating crew member via API');
        const updatedCrewMember = await CrewMemberAPI.update(crewMemberId, updateData);

        console.log('CrewMember Hook: Crew member updated successfully via API', {
          crewMemberId: updatedCrewMember.id,
          name: updatedCrewMember.name,
          updatedFields: Object.keys(updateData)
        });

        // Convert API response to CrewMember instance
        const updatedInstance = CrewMember.fromJSON(updatedCrewMember);

        // Update local state with server response (replaces optimistic update)
        const finalUpdatedCrewMembers = [...crewMembers];
        finalUpdatedCrewMembers[existingCrewMemberIndex] = updatedCrewMember;
        setCrewMembers(finalUpdatedCrewMembers);

        // Update cache with the final data (single cache update, like customers)
        saveCrewMembersToStorage(finalUpdatedCrewMembers);

        console.log('CrewMember Hook: Successfully updated crew member in local state and cache');

        // Invalidate related caches that depend on crew member data
        // This is similar to how appointments handle cross-week cache invalidation
        invalidateRelatedCaches(crewMemberId, updateData);

        return updatedInstance;

      } catch (apiError) {
        // CRITICAL: Rollback optimistic update on API failure (like customers)
        console.error('CrewMember Hook: API call failed, rolling back optimistic update', {
          crewMemberId,
          error: apiError.message
        });

        setCrewMembers(originalCrewMembers);
        console.log('CrewMember Hook: Rolled back to original state');

        // Re-throw the API error
        throw apiError;
      }

    } catch (err) {
      console.error('CrewMember Hook: Error updating crew member:', {
        crewMemberId,
        updateFields: Object.keys(updateData || {}),
        error: err.message
      });

      // Ensure state is restored to original if not already done
      if (crewMembers !== originalCrewMembers) {
        setCrewMembers(originalCrewMembers);
        console.log('CrewMember Hook: Restored original state after error');
      }

      const errorMessage = `Failed to update crew member: ${err.message}`;
      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setLoading(false);
    }
  }, [crewMembers, crewMemberInstances]);

  // Helper function to invalidate related caches (similar to appointment cross-week invalidation)
  const invalidateRelatedCaches = useCallback((crewMemberId, updateData) => {
    try {
      // If crew member services were updated, invalidate appointment cache
      // since appointments depend on crew member service assignments
      if (updateData.assigned_services !== undefined) {
        console.log('CrewMember Hook: Services updated, invalidating appointment cache');

        // Clear appointment cache metadata to force reload
        // This is similar to how appointments handle week cache invalidation
        localStorage.removeItem('salon_appointments_cache_metadata');

        // Clear all week-based appointment caches
        const cachedWeeks = localStorage.getItem('salon_appointments_cache_metadata');
        if (cachedWeeks) {
          try {
            const weeks = JSON.parse(cachedWeeks);
            weeks.forEach(weekStart => {
              localStorage.removeItem(`salon_appointments_cache_week_${weekStart}`);
            });
          } catch (e) {
            console.warn('Error clearing appointment week caches:', e);
          }
        }
      }

      // If crew member name/color was updated, these changes affect UI display
      // but don't require cache invalidation for other entities
      if (updateData.name || updateData.color) {
        console.log('CrewMember Hook: Display properties updated, no cross-cache invalidation needed');
      }

    } catch (error) {
      console.warn('CrewMember Hook: Error invalidating related caches:', error);
      // Don't throw - cache invalidation failure shouldn't break the update
    }
  }, []);

  // Fixed deleteCrewMember function in useCrewMembersPG.js
  // Replace the existing incomplete deleteCrewMember function with this:

  const deleteCrewMember = useCallback(async (crewMemberId) => {
    setLoading(true);
    setError(null);

    // Store original state for potential rollback
    const originalCrewMembers = [...crewMembers];

    try {
      // Find the crew member index
      const existingCrewMemberIndex = crewMembers.findIndex(m => m.id === crewMemberId);
      if (existingCrewMemberIndex === -1) {
        throw new Error('Crew member not found');
      }

      // Optimistic update: Update local state immediately
      const optimisticUpdatedCrewMembers = [...crewMembers];
      optimisticUpdatedCrewMembers[existingCrewMemberIndex] = {
        ...optimisticUpdatedCrewMembers[existingCrewMemberIndex],
        is_active: false,
        updated_at: new Date().toISOString()
      };

      setCrewMembers(optimisticUpdatedCrewMembers);
      console.log('CrewMember Hook: Applied optimistic deactivation to local state');

      try {
        // Make the API call
        console.log('CrewMember Hook: Deactivating crew member via API');
        const deactivatedMember = await CrewMemberAPI.delete(crewMemberId);

        console.log('CrewMember Hook: Crew member deactivated successfully via API', {
          crewMemberId: deactivatedMember.id,
          name: deactivatedMember.name,
          isActive: deactivatedMember.is_active
        });

        // Update local state with server response (replaces optimistic update)
        const finalUpdatedCrewMembers = [...crewMembers];
        finalUpdatedCrewMembers[existingCrewMemberIndex] = {
          ...finalUpdatedCrewMembers[existingCrewMemberIndex],
          is_active: deactivatedMember.is_active,
          updated_at: deactivatedMember.updated_at
        };

        setCrewMembers(finalUpdatedCrewMembers);

        // Update cache with the final data
        saveCrewMembersToStorage(finalUpdatedCrewMembers);

        console.log('CrewMember Hook: Successfully deactivated crew member in local state and cache');

        return deactivatedMember;

      } catch (apiError) {
        // CRITICAL: Rollback optimistic update on API failure
        console.error('CrewMember Hook: API call failed, rolling back optimistic update', {
          crewMemberId,
          error: apiError.message
        });

        setCrewMembers(originalCrewMembers);
        console.log('CrewMember Hook: Rolled back to original state');

        // Re-throw the API error
        throw apiError;
      }

    } catch (err) {
      console.error('CrewMember Hook: Error deactivating crew member:', {
        crewMemberId,
        error: err.message
      });

      // Ensure state is restored to original if not already done
      if (crewMembers !== originalCrewMembers) {
        setCrewMembers(originalCrewMembers);
        console.log('CrewMember Hook: Restored original state after error');
      }

      const errorMessage = `Failed to deactivate crew member: ${err.message}`;
      setError(errorMessage);
      throw new Error(errorMessage);

    } finally {
      setLoading(false);
    }
  }, [crewMembers]);

  // Get crew member by ID
  const getCrewMemberById = useCallback((crewMemberId) => {
    return crewMemberInstances.find(member => member.id === crewMemberId);
  }, [crewMemberInstances]);

  // Get crew member by name
  const getCrewMemberByName = useCallback((name) => {
    return crewMemberInstances.find(
      member => member.name.toLowerCase() === name.toLowerCase()
    );
  }, [crewMemberInstances]);

  // Get crew member from database by ID (for fresh data)
  const getCrewMember = useCallback(async (crewMemberId) => {
    setError(null);

    try {
      const crewMember = await CrewMemberAPI.getById(crewMemberId);
      return crewMember ? CrewMember.fromJSON(crewMember) : null;
    } catch (err) {
      console.error('Error getting crew member:', err);
      setError(err.message);
      throw err;
    }
  }, []);

  // Search crew members using PostgreSQL full-text search
  const searchCrewMembers = useCallback((searchTerm) => {
    if (!searchTerm.trim()) return crewMembers;

    const lowerSearch = searchTerm.toLowerCase();
    return crewMembers.filter(member => {
      return (
        member.name?.toLowerCase().includes(lowerSearch) ||
        member.email?.toLowerCase().includes(lowerSearch) ||
        member.phone?.includes(searchTerm) ||
        member.assigned_services?.some(service =>
          service.name?.toLowerCase().includes(lowerSearch)
        )
      );
    });
  }, [crewMembers]);

  // Get crew members by service ID
  const getCrewMembersByService = useCallback((serviceId) => {
    return crewMemberInstances.filter(member =>
      member.is_active && member.assigned_services.includes(serviceId)
    );
  }, [crewMemberInstances]);

  // Get available (active) crew members for a specific service
  const getAvailableCrewMembersForService = useCallback((serviceId) => {
    return crewMemberInstances.filter(member =>
      member.is_active && member.assigned_services.includes(serviceId)
    );
  }, [crewMemberInstances]);

  // Get crew member statistics - enhanced with PostgreSQL analytics
  const getCrewMemberStats = useCallback(() => {
    const totalMembers = crewMembers.length;
    const activeMembers = activeCrewMembers.length;
    const totalSpecialties = new Set(
      crewMembers.flatMap(member =>
        member.assigned_services?.map(service => service.id) || []
      )
    ).size;

    return {
      totalMembers,
      activeMembers,
      inactiveMembers: totalMembers - activeMembers,
      totalSpecialties
    };
  }, [crewMembers, activeCrewMembers]);

  // Handle crew member submission (add or edit) - maintains same interface
  const handleCrewMemberSubmit = useCallback(async (crewMemberDataOrId, modeOrData, mode) => {
    try {
      if (mode === 'edit') {
        // crewMemberDataOrId is the ID, modeOrData is the update data
        await updateCrewMember(crewMemberDataOrId, modeOrData);
        console.log('Crew member updated successfully');
        return { success: true, mode: 'edit' };
      } else {
        // crewMemberDataOrId is the new crew member data
        await addCrewMember(crewMemberDataOrId);
        console.log('Crew member added successfully');
        return { success: true, mode: 'add' };
      }
    } catch (error) {
      console.error('Error saving crew member:', error);
      throw error; // Re-throw so the calling component can handle it
    }
  }, [addCrewMember, updateCrewMember]);

  // Validate appointment service assignment
  const validateAppointmentServiceAssignment = useCallback((appointment) => {
    if (!appointment.service_id || !appointment.crew_member_id) {
      return { isValid: true }; // Can't validate incomplete appointments
    }

    const crewMember = crewMemberInstances.find(cm => cm.id === appointment.crew_member_id);
    if (!crewMember) {
      return { isValid: false, error: 'Crew member not found' };
    }

    const canPerform = crewMember.assigned_services?.includes(appointment.service_id);
    return {
      isValid: canPerform,
      error: canPerform ? null : 'Crew member cannot perform this service'
    };
  }, [crewMemberInstances]);

  // Check crew member availability
  const checkCrewMemberAvailability = useCallback((crewMemberId, date, time) => {
    const crewMember = getCrewMemberById(crewMemberId);
    if (!crewMember) return { available: false, error: 'Crew member not found' };

    return {
      available: crewMember.isAvailableAt ? crewMember.isAvailableAt(getDayNameFromDate(date), time) : true,
      conflict: crewMember.getAvailabilityConflict ? crewMember.getAvailabilityConflict(date, time) : null
    };
  }, [getCrewMemberById]);

  // Get crew member availability
  const getCrewMemberAvailability = useCallback((crewMemberId) => {
    const crewMember = getCrewMemberById(crewMemberId);
    return crewMember ? crewMember.availability : null;
  }, [getCrewMemberById]);

  // Helper function to get day name from date
  const getDayNameFromDate = useCallback((dateString) => {
    const date = new Date(dateString);
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    return dayNames[date.getDay()];
  }, []);

  return {
    // Data - maintaining same interface as Firebase hook
    crewMembers: crewMemberInstances,
    activeCrewMembers,
    loading,
    error,

    // Actions - same interface as Firebase hook
    addCrewMember,
    updateCrewMember,
    deleteCrewMember,
    getCrewMember,
    clearError,
    handleCrewMemberSubmit,

    // Query methods - same interface as Firebase hook
    getCrewMemberById,
    getCrewMemberByName,
    searchCrewMembers,
    getCrewMembersByService,
    getAvailableCrewMembersForService,
    validateAppointmentServiceAssignment,
    checkCrewMemberAvailability,
    getCrewMemberAvailability,

    // Utility methods - same interface as Firebase hook
    getCrewMemberStats,

    // PostgreSQL-specific methods
    loadAllCrewMembers, // New method for explicit loading

    // Legacy compatibility
    crewMemberInstances, // For backward compatibility
  };
}